/* eslint-disable max-len */
import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { useSelector } from 'react-redux'
import errorRedirect from '../../utils/errorRedirect'
import {
  AdSlot,
  PlayerApiFetch,
  ProgrammeHeader,
  Puff,
  Spinner,
  Scroller,
  Tabs,
  Tab,
  PageFooter,
} from '..'
import { remapCardContent } from './summaryData/getTabs'
import styles from './styles'
import { API_IMAGE_OBJECT, CARD_OBJECT, ERROR_OBJECT } from '../../utils/types'
// import errorRedirect from '../../utils/errorRedirect'

const Summary = ({
  error,
  data,
  meta,
  currentUrl,
  currentDomain,
}) => {
  const users = useSelector(stateSelector => stateSelector.user)
  const [isMobile, setIsMobile] = useState(true)
  useEffect(() => {
    updateLayout()
    window.addEventListener('resize', debounce(updateLayout, 300))

    return () => { window.removeEventListener('resize', debounce(updateLayout, 300)) }
  }, [])

  const updateLayout = () => {
    if (window.innerWidth >= 730) {
      setIsMobile(false)
    } else {
      setIsMobile(true)
    }
  }

  if (error) {
    const theError = error
    theError.currentUrl = currentUrl
    // eslint-disable-next-line consistent-return
    return errorRedirect()
  }
  const {
    programmeGuid,
    programmeHeader,
    featuredEpisode,
    featuredShortform,
    puffs,
    tabs,
    programmeData,
    searchTitle,
    newsShortform,
    stvLiveData,
    accessibility,
  } = data

  const adSlotProps = {
    title: programmeGuid,
    programCategory: data?.programmeData?.genre?.name,
    contentProvider: data?.programmeData?.distributor,
    categoryAssoc: data?.programmeData?.searchCategories ? data.programmeData.searchCategories.slice(-1)[0] : null,
    guid: programmeGuid,
  }

  const mainTab = [{ id: 'episode', title: 'Episodes' },
    { id: 'extra', title: 'Extras' },
    { id: 'news-at-six', title: 'News at Six' },
    { id: 'more', title: 'More like this' },
    { id: 'advicePuff', title: 'Advice and Helplines' },
    { id: 'recipePuff', title: 'Recipes, Blogs and more' }]

  const episodes = tabs?.filter(tab => tab.type !== 'video' && !tab.accessibility)
  const advicePuff = puffs?.filter(puff => puff?.title?.includes('Advice'))
  const recipePuff = puffs?.filter(puff => !puff?.title?.includes('Advice') && !puff?.title?.includes('Apply'))
  const applyTab = puffs?.filter(puff => puff?.title?.includes('Apply'))
  const adTabs = tabs?.filter(tab => tab?.accessibility === 'ad')
  const vsTabs = tabs?.filter(tab => tab?.accessibility === 'vs')

  if (applyTab?.length > 0 && applyTab?.[0]?.link) {
    mainTab.push({ id: 'applyTab', title: 'Apply', link: applyTab[0].link })
  }

  if (accessibility?.audioDescribed && adTabs?.length > 0) {
    mainTab.splice(1, 0, { id: 'ad', title: 'Audio Described' })
  }

  if (accessibility?.visuallySigned && vsTabs?.length > 0) {
    mainTab.splice(2, 0, { id: 'vs', title: 'Visually Signed' })
  }

  const removeObjectWithId = (id) => {
    const objWithIdIndex = mainTab.findIndex(obj => obj.id === id)
    mainTab.splice(objWithIdIndex, 1)
  }

  const getTabs = (type) => {
    switch (type) {
      case 'ad': return adTabs
      case 'vs': return vsTabs
      default: return episodes
    }
  }

  const isStvLiveProg = stvLiveData?.now?.programme?.guid === programmeGuid
  if (
    programmeData?.searchCategories?.includes('Movies') // Hide episode tab for movies
    || (!isStvLiveProg && programmeData?.availableEpisodes === 1 && !adTabs) // Hide episode tab if only 1 episode and programme is not currently on live
  ) {
    removeObjectWithId('episode')
  }
  if (programmeData.availableEpisodes === 0) {
    removeObjectWithId('episode')
  }
  if (featuredShortform === null || !featuredShortform?.length) {
    removeObjectWithId('extra')
  }
  if (newsShortform === null || !newsShortform?.length) {
    removeObjectWithId('news-at-six')
  }
  if (data.recommended < 1 || typeof data.recommended === 'undefined') {
    removeObjectWithId('more')
  }
  if (advicePuff < 1) {
    removeObjectWithId('advicePuff')
  }
  if (recipePuff < 1) {
    removeObjectWithId('recipePuff')
  }

  const { searchCategories } = programmeData
  const { image } = featuredEpisode || {}
  const searchImgSrc = image?._renditions && programmeData.images?.[0]?._filepath ? programmeData.images?.[0]?._filepath : false

  const posterImage = programmeData?.images.find(img => img?.imageType === 'PPMB')?._filepath
  const posterImageUrl = posterImage ? posterImage.replace(/\[width\]/g, '300').replace(/\[height\]/g, '400') : null
  const imageWithPoster = {
    ...image,
    _renditions: {
      ...(image?._renditions || {}),
      '300x400': posterImageUrl || null,
    },
  }

  return (
    <main className="Summary">
      <Head>
        <meta className="swiftype" name="title" data-type="string" content={searchTitle} />
        <meta className="swiftype" name="resultDescriptionTx" data-type="text" content={programmeHeader?.longDescription} />
        {
          image && <meta className="swiftype" name="customImage" data-type="enum" content={JSON.stringify(imageWithPoster)} />
        }
        {
          searchImgSrc && <meta className="swiftype" name="image" data-type="enum" content={searchImgSrc} />
        }
        {
          (Array.isArray(searchCategories) && searchCategories.length > 0)
          && searchCategories.map(category => (
            <meta name="category" content={category} className="swiftype" data-type="string" key={category} />
          ))
        }
        <meta name="og:title" content="{programmeHeader.name}" />
        <meta name="og:description" content="{programmeHeader.longDescription}" />
      </Head>

      <div className="AdSlot-container" style={isMobile ? { display: 'none' } : {}}>
        <AdSlot type="leaderboard" path="summary" programmeOrChannelGuid={programmeGuid} adSlotProps={adSlotProps} />
      </div>

      { programmeHeader && (
        <ProgrammeHeader
          {...programmeHeader}
          featuredEpisode={featuredEpisode}
          programmeData={programmeData}
          stvLiveData={stvLiveData}
          programmeGuid={programmeGuid}
          forceAds={programmeData?.forceAds}
          svodExclusive={programmeData?.svodExclusive}
          isMobile={isMobile}
        />
      )}
      {mainTab.length > 0 ? (
        <section className="featureTab-Wrapper">
          <Tabs tabs={mainTab} programmeTab programmeGuid={programmeGuid} adSlotProps={adSlotProps}>
            { mainTab.map(tab => (
              <Tab
                key={tab.id}
                id={tab.id}
              >
                <ul>
                  {(tab.id === 'episode' || tab.id === 'ad' || tab.id === 'vs') && (
                    <li className="featured-tab-item" key={tab.id}>
                      { getTabs(tab.id)?.[0]?.title !== 'Episodes' && getTabs(tab.id)?.[0]?.title !== 'Audio Described Episodes' && getTabs(tab.id)?.[0]?.title !== 'Visually Signed Episodes' && programmeHeader.availableEpisodes >= 1 ? (
                        <section className="featured-tabs">
                          <Tabs tabs={getTabs(tab.id)} subTab programmeGuid={programmeGuid}>
                            { getTabs(tab.id)?.map(subtab => (
                              <Tab
                                key={subtab.id}
                                id={subtab.id}
                              >
                                <div className="subtabclass">
                                  {subtab.data && (
                                    <Scroller
                                      assets={subtab.data}
                                      recoID={null}
                                      programmeTab
                                      adSlotProps={adSlotProps}
                                      isMLT={false}
                                      isAdSlot
                                      playIcon
                                    />
                                  )}

                                  {/* otherwise, prepare for lazy loading content when activated */}
                                  { !subtab.data && subtab?.params && (
                                    <PlayerApiFetch
                                      params={subtab.params}
                                      success={(results) => {
                                        if (!results) return null
                                        const resultData = results.map(item => remapCardContent({ item, type: subtab.type }))
                                        return (
                                          <Scroller
                                            assets={resultData}
                                            recoID={null}
                                            programmeTab
                                          />
                                        )
                                      }}
                                      fetching={() => <div className="tab-loader"><Spinner /></div>}
                                      fail={() => (
                                        <p>Sorry, we couldn't load that content. Try refreshing this page.</p>
                                      )}
                                    />
                                  )}
                                </div>
                              </Tab>
                            ))}
                          </Tabs>

                        </section>
                      )
                        : (
                          <section className="featured-tabs">
                            {getTabs(tab.id)?.[0]?.data && (
                              <Scroller
                                assets={getTabs(tab.id)[0]?.data}
                                recoID={null}
                                programmeTab
                                isMLT={false}
                                isAdSlot
                                adSlotProps={adSlotProps}
                              />
                            )}
                            {!getTabs(tab.id)?.[0]?.data && (
                              <PlayerApiFetch
                                params={getTabs(tab.id)[0]?.params}
                                success={(results) => {
                                  if (!results) return null
                                  const resultData = results.map(item => remapCardContent({ item, type: getTabs(tab.id)[0].type }))
                                  return (
                                    <Scroller
                                      assets={resultData}
                                      recoID={null}
                                      programmeTab
                                    />
                                  )
                                }}
                                fetching={() => <div className="tab-loader"><Spinner /></div>}
                                fail={() => (
                                  <p>Sorry, we couldn't load that content. Try refreshing this page.</p>
                                )}
                              />
                            )}
                          </section>
                        )}
                    </li>
                  )}

                  {tab.id === 'extra' && (
                    featuredShortform?.length > 0 && (
                      <li className="featured-tab-item" key={tab.id}>
                        <section className="featured-tabs">
                          <Scroller
                            assets={featuredShortform}
                            recoID={null}
                            programmeTab
                          />
                        </section>
                      </li>
                    )
                  )}

                  {tab.id === 'news-at-six' && (
                    newsShortform !== null && (
                      <li className="news-at-six" key={tab.id}>
                        <section className="featured-tabs">
                          <Scroller
                            assets={newsShortform}
                            recoID={null}
                            programmeTab
                          />

                        </section>
                      </li>
                    )
                  )}

                  {tab.id === 'more' && data.recommended && (
                    <section className="recommended" data-swiftype-index="false">
                      {!users?.currentlySubscribed
                        ? (
                          <Scroller
                            assets={data.recommended}
                            recoID={meta?.analytics?.personalisationRequestID || meta?.analytics?.recommendationRequestId}
                            recommendedTab
                            programmeTab
                            isMLT
                            isAdSlot
                            adSlotProps={adSlotProps}
                          />
                        ) : (
                          <Scroller
                            assets={data.recommended}
                            recoID={meta?.analytics?.personalisationRequestID || meta?.analytics?.recommendationRequestId}
                            recommendedTab
                            liveTab={false}
                          />
                        )}
                    </section>
                  )}
                  {tab.id === 'advicePuff' && advicePuff && (
                    <ul className="featured-puff">
                      { advicePuff.map(puff => (
                        <Puff key={puff?.id} link={puff?.link} image={puff?.image} />
                      ))}
                    </ul>
                  )}
                  {tab.id === 'recipePuff' && recipePuff && (

                    <ul className="featured-puff">
                      {recipePuff.map(puff => (
                        <Puff key={puff?.id} link={puff?.link} image={puff?.image} />
                      ))}
                    </ul>
                  )}
                </ul>
              </Tab>
            ))}
          </Tabs>
          <PageFooter currentDomain={currentDomain} />
        </section>
      )
        : (
          <React.Fragment>
            <section className="footer-wrapper">
              <div className="sponsor" style={isMobile ? { display: 'none' } : {}}>
                <AdSlot type="sponsor-logo" path="summary" programmeOrChannelGuid={programmeGuid} adSlotProps={adSlotProps} />
              </div>
            </section>
            <div className="mpu-container">
              <AdSlot type="medium-rectangle" path="summary" programmeOrChannelGuid={programmeGuid} adSlotProps={adSlotProps} />
            </div>
            <PageFooter currentDomain={currentDomain} />

          </React.Fragment>
        )}

      <style jsx>{styles}</style>
    </main>
  )
}

const children = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
])

Summary.propTypes = {
  error: ERROR_OBJECT,
  currentUrl: PropTypes.string.isRequired,
  currentlySubscribed: PropTypes.bool,
  data: PropTypes.shape({
    programmeGuid: PropTypes.string,
    programmeHeader: PropTypes.shape({
      name: PropTypes.string,
      availableEpisodes: PropTypes.number,
      longDescription: PropTypes.string,
      image: API_IMAGE_OBJECT,
    }),
    programmeData: PropTypes.shape({
      searchCategories: PropTypes.arrayOf(PropTypes.string),
      genre: PropTypes.shape({
        name: PropTypes.string,
      }),
      distributor: PropTypes.string,
      images: PropTypes.arrayOf(API_IMAGE_OBJECT),
      forceAds: PropTypes.bool,
      svodExclusive: PropTypes.bool,
      svodProhibited: PropTypes.bool,
      vipExclusive: PropTypes.bool,
      vipProhibited: PropTypes.bool,
      availableEpisodes: PropTypes.number,
    }),
    newsShortform: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        subTitle: PropTypes.string,
        image: PropTypes.object,
        content: PropTypes.string,
        link: PropTypes.string,
        reporters: PropTypes.arrayOf(PropTypes.object),
      })
    ),
    stvLiveData: PropTypes.object,
    tabs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        children,
      })
    ),
    nextEpisode: PropTypes.shape({
      title: PropTypes.string,
      startTime: PropTypes.string,
    }),
    puffs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      link: PropTypes.string,
      image: API_IMAGE_OBJECT,
      title: PropTypes.string,
    })),
    featuredEpisode: CARD_OBJECT,
    recommended: PropTypes.arrayOf(CARD_OBJECT),
    featuredShortform: PropTypes.arrayOf(CARD_OBJECT),
    searchTitle: PropTypes.string.isRequired,
    accessibility: PropTypes.shape({
      audioDescribed: PropTypes.bool,
      visuallySigned: PropTypes.bool,
    }),
  }),
  meta: PropTypes.shape({
    analytics: PropTypes.shape({
      personalisationRequestID: PropTypes.string,
      recommendationRequestId: PropTypes.string,
    }),
  }),
  currentDomain: PropTypes.string,
}

Summary.defaultProps = {
  error: null,
  currentlySubscribed: false,
  data: {
    programmeData: {
      searchCategories: [],
      forceAds: false,
      svodExclusive: false,
      svodProhibited: false,
      vipExclusive: false,
      vipProhibited: false,
      availableEpisodes: 0,
      distributor: '',
      stvLiveData: {},
      genre: {
        name: '',
      },
    },
    accessibility: {},
  },
  meta: {
    analytics: { personalisationRequestID: '' },
  },
  currentDomain: '',
}

export default Summary
